<template>
  <div class="attention-wrap">
    <div class="filter-outerblk">
      <div class="single-fieldsouter search-field">
        <v-text-field
          v-model="filter.searchquery"
          label="Trip ID / Name / Mobile"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </div>

      <div class="single-fieldsouter">
        <v-autocomplete
          v-model="filter.status"
          :items="statusOptions"
          label="Reason"
          item-text="label"
          item-value="value"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
          @change="getTripListing"
        ></v-autocomplete>
      </div>

      <div class="single-fieldsouter">
        <v-menu
          v-model="dateModal"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field v-model="dateLabel" label="Date Range" readonly v-on="on" outlined dense hide-details></v-text-field>
          </template>
          <v-date-picker v-model="filter.dateRange" range color="#d59e1d" no-title @change="onDateChange"></v-date-picker>
        </v-menu>
      </div>

      <div class="reset-outer">
        <v-btn class="lighter-btn" color="#ff9600" @click="getTripListing" text>
          <i class="fas fa-redo"></i>
        </v-btn>
      </div>
      <div class="reset-outer">
        <v-btn class="reset-btn" color="#ff9500" small @click="reset">Reset</v-btn>
      </div>
    </div>

    <div class="table-outerblk">
      <v-overlay absolute :value="isLoading" :opacity="0.7" color="#ffffff" z-index="4">
        <v-progress-circular indeterminate :size="50" :width="5" color="#d59e1d"></v-progress-circular>
      </v-overlay>

      <v-simple-table fixed-header v-if="trips.length > 0">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Trip ID</th>
              <th class="text-left">Crew Details</th>
              <th class="text-left">Pickup Address</th>
              <th class="text-left">Drop Address</th>
              <th class="text-left">Pickup Time</th>
              <th class="text-left">Reporting Time</th>
              <th class="text-left">Reason</th>
              <th class="text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="trip in trips"
              :key="trip.tripId"
              class="critical-row clickable-row"
              @click="openDetails(trip)"
            >
              <td>
                <div class="stack-cell">
                  <span class="strong-text">{{ trip.displayId || trip.tripId }}</span>
                  <span class="muted-text">{{ serviceTypeName(trip.serviceType) }}</span>
                </div>
              </td>
              <td>
                <CrewDetails
                  :employees="trip.employees"
                  :fallbackName="trip.name"
                  :fallbackMobile="trip.mobile"
                />
              </td>
              <td class="address-cell">{{ addressOf(trip.pickup) || trip.source || "--" }}</td>
              <td class="address-cell">{{ addressOf(trip.dropoff) || trip.destination || "--" }}</td>
              <td>{{ formatDate(trip.pickupDate) }}</td>
              <td>{{ formatDate(trip.reportingTime) }}</td>
              <td>
                <span class="status-chip" :style="{ background: statusColor(trip.uberStatus) }">
                  {{ statusLabel(trip.uberStatus) }}
                </span>
                <!-- Uber's own words for the refusal. Only a booking_failed row
                     has one - the other two statuses come off a webhook that
                     says what happened but never why. -->
                <div class="reason-detail" v-if="failureReasonOf(trip)" :title="failureReasonOf(trip)">
                  {{ failureReasonOf(trip) }}
                </div>
              </td>
              <td>
                <!-- "Book Again", not "Re-book", when no booking was ever made:
                     there is nothing to book *again* in the other sense, and the
                     dialog behind it works differently. -->
                <v-btn small color="#d59e1d" class="rebook-btn" @click.stop="openRebook(trip)">
                  <i class="fas fa-redo"></i>&nbsp; {{ isBookingFailed(trip) ? "Book Again" : "Re-book" }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div class="empty-state" v-else-if="!isLoading">
        <i class="fas fa-check-circle"></i>
        <span>Nothing needs attention.</span>
      </div>
    </div>

    <div class="pagination" v-if="total > 0">
      <span class="count-text">{{ total }} trips</span>
      <v-pagination
        v-model="page"
        :length="pageLength"
        :total-visible="6"
        :disabled="isLoading"
        @input="fetchTrips"
      ></v-pagination>
    </div>

    <!-- The details drawer is hosted by UberTrips, outside the tab panel. -->

    <!-- ------------------------------------------------------------------ -->
    <!-- Re-book dialog                                                      -->
    <!-- ------------------------------------------------------------------ -->
    <v-dialog v-model="rebookDialog" width="860" persistent scrollable content-class="uber-estimate-dialog">
      <v-card class="uber-estimate-card" elevation="0" v-if="rebookTrip">
        <div class="uep-header">
          <div class="uep-header-left">
            <div class="uep-uber-wordmark">
              <img :src="require('@/assets/images/uber/uberLogo.webp')" alt="Uber" class="uep-wordmark-img" />
            </div>
            <div class="uep-header-divider"></div>
            <div>
              <div class="uep-title">Re-book ride</div>
              <div class="uep-subtitle">
                <div class="uep-subtitle-row">
                  <v-icon size="13" color="rgba(255,255,255,0.6)">mdi-alert-circle-outline</v-icon>
                  <span>{{ statusLabel(rebookTrip.uberStatus) }} &mdash; trip #{{ rebookTrip.displayId || rebookTrip.tripId }}</span>
                </div>
              </div>
            </div>
          </div>
          <v-btn icon dark @click="closeRebook" class="uep-close-btn">
            <v-icon size="22">mdi-close</v-icon>
          </v-btn>
        </div>

        <!--
          Read-only. A re-book dispatches on demand once the original slot has
          gone by, so there is nothing to edit. The mode label is only shown
          when the server has actually told us one - it comes back with the
          estimate, and the browser clock must not decide it.
        -->
        <div class="uep-schedule">
          <div class="uep-schedule-left">
            <v-icon size="16" color="#1a1a1a">mdi-calendar-clock</v-icon>
            <span class="uep-schedule-label">Pickup</span>
            <span class="uep-schedule-value">{{ pickupLabel }}</span>
          </div>
        </div>

        <!--
          Says what will actually happen, in plain words. "On demand" is Uber's
          term, not something ops needs to learn.
        -->
        <div class="uep-note" :class="{ 'uep-note--now': sendNow }">
          <v-icon size="15" :color="sendNow ? '#0f766e' : '#8a6d1f'">
            {{ sendNow ? "mdi-car-clock" : "mdi-calendar-check" }}
          </v-icon>
          <span v-if="sendNow">
            This pickup time has already passed, so the cab will be sent
            <strong>right away</strong>.
          </span>
          <span v-else>
            The cab will be arranged for the pickup time above.
          </span>
        </div>

        <v-card-text class="uep-body">
          <!-- Step 1: which vehicle.
               Hidden on Book Again - there is no previous booking whose class
               could be reused, so the only route is a fresh estimate. -->
          <div class="choice-row" v-if="!bookAgainMode">
            <div class="choice-card" :class="{ selected: choice === 'same' }" @click="selectChoice('same')">
              <v-icon size="18" :color="choice === 'same' ? '#1a1a1a' : '#8a8a8a'">mdi-repeat</v-icon>
              <span class="choice-name">Same vehicle class</span>
              <span class="choice-hint">Re-book exactly what the failed trip asked for.</span>
            </div>
            <div class="choice-card" :class="{ selected: choice === 'change' }" @click="selectChoice('change')">
              <v-icon size="18" :color="choice === 'change' ? '#1a1a1a' : '#8a8a8a'">mdi-car-multiple</v-icon>
              <span class="choice-name">Choose a different vehicle</span>
              <span class="choice-hint">Fetch fresh estimates and pick another class.</span>
            </div>
          </div>

          <!-- The crew number is checked with Uber before any estimate runs, the
               same as the booking flow. A landline or an SMS opt-out cannot
               receive the ride links, and it is better said here than at the
               moment of booking. -->
          <div class="uep-loading" v-if="verifyingPhone">
            <v-progress-circular indeterminate color="#000" size="40" width="3"></v-progress-circular>
            <p class="uep-loading-title">Verifying mobile number</p>
          </div>

          <div class="uep-empty uep-empty--error" v-else-if="phoneError">
            <v-icon size="34" color="#c62828">mdi-phone-alert-outline</v-icon>
            <p class="uep-empty-title">Cannot book for this number</p>
            <p class="uep-empty-sub">{{ phoneError }}</p>
            <p class="uep-empty-sub" v-if="phoneOptIn">{{ phoneOptIn }}</p>
          </div>

          <!-- Step 2: when changing vehicle, and always on Book Again -->
          <template v-else-if="choice === 'change' || bookAgainMode">
            <div class="uep-loading" v-if="estimating">
              <v-progress-circular indeterminate color="#000" size="40" width="3"></v-progress-circular>
              <p class="uep-loading-title">Finding available rides</p>
            </div>

            <div class="inline-warning" v-else-if="estimateError">
              <i class="fas fa-exclamation-triangle"></i>
              {{ estimateError }}
            </div>

            <div class="uep-list" v-else-if="products.length > 0">
              <div
                v-for="product in products"
                :key="product.productId"
                class="uep-row"
                :class="{ 'uep-row--selected': selectedProduct === product.productId }"
                @click="selectedProduct = product.productId"
              >
                <div
                  class="uep-img-wrap"
                  :style="product.backGroundImage ? `background-image:url('${product.backGroundImage}')` : ''"
                >
                  <img v-if="product.image" :src="product.image" :alt="product.category" class="uep-vehicle-img" />
                  <v-icon v-else size="44" color="#aaa">mdi-car</v-icon>
                </div>
                <div class="uep-info">
                  <div class="uep-name-row">
                    <span class="uep-name">{{ product.category }}</span>
                    <span class="uep-badge" :class="`uep-badge--${bookingTypeOf(product)}`">
                      {{ bookingTypeLabel(product) }}
                    </span>
                  </div>
                  <div class="uep-meta">
                    <span class="uep-meta-item" v-if="product.capacity">
                      <v-icon size="12" color="#6b6b6b">mdi-account-multiple-outline</v-icon>
                      {{ product.capacity }} seats
                    </span>
                    <span class="uep-meta-dot"></span>
                    <span class="uep-meta-item">
                      <v-icon size="12" color="#6b6b6b">mdi-clock-outline</v-icon>
                      {{ durationOf(product) }}
                    </span>
                  </div>
                  <div class="uep-time-rows">
                    <div class="uep-eta-row">
                      <v-icon size="13" color="#6b6b6b">mdi-clock-start</v-icon>
                      <span>Pickup By: <strong>{{ product.pickupEstimate || "---" }}</strong></span>
                    </div>
                    <div class="uep-eta-row">
                      <v-icon size="13" color="#276EF1">mdi-navigation-variant</v-icon>
                      <span>Drop By: <strong>{{ (product.trip && product.trip.eta) || "---" }}</strong></span>
                    </div>
                  </div>
                </div>
                <div class="uep-check" :class="{ 'uep-check--on': selectedProduct === product.productId }">
                  <v-icon size="18" color="white">mdi-check</v-icon>
                </div>
              </div>
            </div>

            <div class="uep-empty" v-else>
              <v-icon size="48" color="#d0d0d0">mdi-car-off</v-icon>
              <p class="uep-empty-title">No rides available</p>
            </div>
          </template>
        </v-card-text>

        <div class="uep-footer">
          <v-spacer></v-spacer>
          <v-btn text class="uep-btn-cancel" @click="closeRebook">Cancel</v-btn>
          <v-btn
            v-if="choice === 'change'"
            text
            class="uep-btn-cancel"
            :loading="estimating"
            @click="getEstimate"
          >
            Refresh
          </v-btn>
          <v-btn class="uep-btn-confirm" :loading="booking" :disabled="!canBook" @click="confirmRebook" elevation="0">
            Confirm Re-book
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <Confirm ref="confirm"></Confirm>
  </div>
</template>

<script>
import axios from "@/axios/axios-global";
import moment from "moment";
import toast from "@/components/common/toast";
import { readApiError } from "./apiError";
import Confirm from "./UberConfirm.vue";
import CrewDetails from "./CrewDetails.vue";
import uberRealtime from "@/services/uberRealtime";
import {
  statusLabel,
  statusColor,
  statusesForGroup,
  STATUS_GROUP,
  UBER_STATUS,
} from "@/config/uberStatus";

export default {
  name: "AttentionTrips",
  components: { Confirm, CrewDetails },
  props: {
    focusTrip: {
      type: [Number, String],
      default: null,
    },
    // Bumped by the shell after a cancel, so the list re-fetches without the
    // shell having to reach in through $refs.
    refreshSignal: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      filter: this.defaultFilter(),
      trips: [],
      isLoading: false,
      dateModal: false,
      searchTimer: null,
      page: 1,
      pageSize: 25,
      total: 0,
      unsubscribe: null,
      currentTrip: 0,

      rebookDialog: false,
      rebookTrip: null,
      // 'rebook' - a booking existed and died, so its own class can be reused.
      // 'bookAgain' - Uber refused outright and there is no booking to reuse,
      // so the flow matches the booking screen: verify the number, estimate,
      // then create. The two share one dialog because they end the same way.
      dialogMode: "rebook",
      verifyingPhone: false,
      phoneError: "",
      phoneOptIn: "",
      // 'same' re-books the failed booking's own product; 'change' fetches
      // fresh estimates so ops can pick another class.
      choice: "same",
      products: [],
      selectedProduct: null,
      schedule: null,
      estimating: false,
      estimateError: "",
      booking: false,
    };
  },
  computed: {
    pageLength() {
      return this.pageSize > 0 ? Math.ceil(this.total / this.pageSize) : 1;
    },
    /**
     * Whether this dialog is booking a refused trip rather than re-booking a
     * dead one. The difference is real: no previous class to reuse, the crew
     * number gets verified first, and it books through /trip/create.
     */
    bookAgainMode() {
      return this.dialogMode === "bookAgain";
    },
    statusOptions() {
      return statusesForGroup(STATUS_GROUP.ATTENTION).map((value) => ({ value, label: statusLabel(value) }));
    },
    dateLabel() {
      const range = this.filter.dateRange || [];
      if (range.length === 0) return "";
      if (range.length === 1) return moment(range[0]).format("DD MMM YYYY");
      const [from, to] = [...range].sort();
      return `${moment(from).format("DD MMM")} - ${moment(to).format("DD MMM YYYY")}`;
    },
    /** The trip's own pickup time, shown as context only. */
    pickupLabel() {
      if (this.schedule && this.schedule.pickupDisplay) return this.schedule.pickupDisplay;
      return this.rebookTrip ? this.formatDate(this.rebookTrip.pickupDate) : "--";
    },

    /**
     * Whether the cab goes out immediately. Both sources are server-computed:
     * `dispatchNow` once an estimate has run, and `pickupPassed` from the
     * listing before that. The browser clock is never consulted.
     */
    sendNow() {
      if (this.schedule && this.schedule.dispatchNow !== undefined) {
        return this.schedule.dispatchNow === true;
      }
      return !!(this.rebookTrip && this.rebookTrip.pickupPassed);
    },
    canBook() {
      if (this.booking) return false;
      /*
       * Book Again always needs a chosen vehicle - there is no previous class to
       * fall back on - and must not be pressable while the number is still being
       * checked or after Uber has refused it.
       */
      if (this.bookAgainMode) {
        if (this.verifyingPhone || this.phoneError || this.estimating) return false;
        return !!this.selectedProduct;
      }
      if (this.choice === "same") return true;
      return !!this.selectedProduct && !this.estimating;
    },
  },
  watch: {
    "filter.searchquery"(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (!val || val.length === 0 || val.length >= 3) this.getTripListing();
      }, 400);
    },
    focusTrip(tripId) {
      if (tripId) this.getTripListing();
    },
    refreshSignal() {
      this.fetchTrips();
    },
  },
  mounted() {
    this.getTripListing();
    // A trip landing in a critical state should appear without a manual refresh.
    this.unsubscribe = uberRealtime.subscribe({
      update: () => this.fetchTrips(),
    });
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    statusLabel,
    statusColor,

    defaultFilter() {
      return { searchquery: "", status: null, dateRange: [] };
    },

    payload(page) {
      const range = [...(this.filter.dateRange || [])].sort();
      return {
        listType: "attention",
        status: this.filter.status || null,
        search: this.filter.searchquery || "",
        startDate: range[0] || null,
        endDate: range[1] || range[0] || null,
        page,
        limit: this.pageSize,
      };
    },

    mapRow(row) {
      const uber = row.uberDetails || {};
      return {
        tripId: row.bigId,
        displayId: row.id,
        name: row.user,
        mobile: row.mobile,
        source: row.source,
        destination: row.destination,
        pickupDate: row.pickUpTime,
        reportingTime: row.reportingTime,
        // Crew rows for the details cell: name, mobile, crewId, flightInfo.
        employees: row.employees || [],
        serviceType: row.serviceType,
        uberStatus: uber.uberStatus || null,
        // Only a booking_failed row carries one - Uber's reason for refusing.
        failureReason: uber.failureReason || "",
        productId: uber.productId || null,
        // Server-computed: the pickup slot has gone by, so a re-book dispatches
        // immediately rather than scheduling.
        pickupPassed: row.pickupPassed === true,
        pickup: uber.pickup || null,
        dropoff: uber.dropoff || null,
      };
    },

    async fetchTrips() {
      this.isLoading = true;
      try {
        const response = await axios.post("uber/trip/booked", this.payload(this.page));
        const data = response.data || {};
        this.trips = (data.list || []).map(this.mapRow);

        // The server only counts on page 1; later pages keep the known total.
        if (data.total !== null && data.total !== undefined) {
          this.total = data.total;
          this.$emit("count", this.total);
        }
      } catch (e) {
        this.trips = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    getTripListing() {
      this.page = 1;
      return this.fetchTrips();
    },

    /* ---------------------------------------------------------------- *
     * Re-book
     * ---------------------------------------------------------------- */

    openDetails(trip) {
      this.currentTrip = trip.tripId;
      this.$emit("open-details", trip.tripId);
    },

    openRebook(trip) {
      this.rebookTrip = trip;
      this.rebookDialog = true;
      this.resetDialog();

      /*
       * A refused booking has nothing to re-book, so it takes the booking
       * screen's route instead: check the crew number with Uber, then estimate.
       * Started here rather than on a button, because there is no decision for
       * the operator to make first.
       */
      if (this.isBookingFailed(trip)) {
        this.dialogMode = "bookAgain";
        this.startBookAgain();
      }
    },

    closeRebook() {
      this.rebookDialog = false;
      this.rebookTrip = null;
      this.resetDialog();
    },

    /**
     * Everything the dialog carries between openings.
     *
     * One place, so a Book Again can never inherit half a re-book's state - a
     * stale product list or a phone error from the previous trip.
     */
    resetDialog() {
      this.dialogMode = "rebook";
      this.choice = "same";
      this.products = [];
      this.selectedProduct = null;
      this.schedule = null;
      this.estimateError = "";
      this.verifyingPhone = false;
      this.phoneError = "";
      this.phoneOptIn = "";
    },

    /** Verify the number, then price the ride. Stops at the first refusal. */
    async startBookAgain() {
      const verified = await this.verifyPhone();
      if (!verified) return;
      await this.getEstimate();
    },

    /**
     * The crew number, checked with Uber before anything is priced.
     *
     * A landline or an SMS opt-out cannot receive the ride links, so it is worth
     * failing here with a clear message rather than at the moment of booking.
     * Returns whether the flow may continue.
     */
    async verifyPhone() {
      if (!this.rebookTrip) return false;
      this.verifyingPhone = true;
      this.phoneError = "";
      this.phoneOptIn = "";
      try {
        const response = await axios.post("uber/phone-info", { tripId: this.rebookTrip.tripId });
        this.phoneOptIn = (response.data && response.data.opt_in_message) || "";
        return true;
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        this.phoneError = this.readError(err, "Could not verify the crew number.");
        this.phoneOptIn = data.opt_in_message || "";
        return false;
      } finally {
        this.verifyingPhone = false;
      }
    },

    selectChoice(choice) {
      if (this.choice === choice) return;
      this.choice = choice;
      this.selectedProduct = null;
      // Estimates are only fetched when they are actually needed.
      if (choice === "change" && this.products.length === 0) this.getEstimate();
    },

    async getEstimate() {
      if (!this.rebookTrip) return;
      this.estimating = true;
      this.estimateError = "";
      this.selectedProduct = null;
      try {
        // reBooking lets the server price an on-demand dispatch for a trip whose
        // original slot has passed, instead of refusing it. No time is sent -
        // the server reads the trip's own and decides.
        const response = await axios.post("uber/trip/estimate", {
          tripId: this.rebookTrip.tripId,
          reBooking: true,
        });
        const data = response.data || {};
        this.products = Array.isArray(data) ? data : data.products || [];
        this.schedule = data.schedule || null;
        if (this.products.length === 1) this.selectedProduct = this.products[0].productId;
      } catch (err) {
        this.products = [];
        this.estimateError = this.readError(err, "Could not load rides for this trip.");
      } finally {
        this.estimating = false;
      }
    },

    async confirmRebook() {
      const confirmed = await this.$refs.confirm.open(
        this.bookAgainMode ? "Book Again" : "Rebook Trip",
        `Do you want to request Uber Trip #${this.rebookTrip.displayId || this.rebookTrip.tripId} again ?`,
        { icon: "fas fa-redo", color: "#000000" }
      );
      if (!confirmed) return;

      /*
       * A refused booking never created an UberTrip, so /trip/rebook has nothing
       * to supersede and would refuse it. It goes through /trip/create, exactly
       * as a first booking does - which is also why the choice of vehicle is
       * always explicit here.
       */
      if (this.bookAgainMode) {
        await this.submitBookAgain();
        return;
      }

      this.booking = true;
      try {
        // No pickupTime: a re-book never moves the trip's own schedule.
        const payload = { tripId: this.rebookTrip.tripId };

        // Omitting productId tells the server to reuse the failed booking's own
        // vehicle class, so the choice never has to be resolved on the client.
        if (this.choice === "change") {
          const product = this.products.find((p) => p.productId === this.selectedProduct) || {};
          payload.productId = product.productId;
          payload.pickupEta = product.pickupEta || 0;
          payload.dropEta = (product.trip && product.trip.dropEta) || 0;
          if (product.fareId) payload.fareId = product.fareId;

          // Only this path ran an estimate. The "same vehicle class" path sends
          // neither field, and the server accepts a booking without them.
          if (this.schedule) {
            if (this.schedule.serverTime) payload.estimatedAt = this.schedule.serverTime;
            payload.dispatchNow = this.sendNow;
          }
        }

        const response = await axios.post("uber/trip/rebook", payload);
        toast.success((response.data && response.data.message) || "Trip re-booked successfully.");

        uberRealtime.clearCritical(this.rebookTrip.tripId);
        this.rebookDialog = false;
        this.fetchTrips();
        this.$emit("rebooked", response.data);
      } catch (err) {
        toast.error(this.readError(err, "Failed to re-book the trip."));
      } finally {
        this.booking = false;
      }
    },

    /**
     * Books a trip Uber refused, through the ordinary create endpoint.
     *
     * Same payload the booking screen sends, including estimatedAt and
     * dispatchNow so the server can judge how old this estimate is - a Book
     * Again is a first booking in every respect except where it was started
     * from.
     */
    async submitBookAgain() {
      const product = this.products.find((p) => p.productId === this.selectedProduct) || {};

      this.booking = true;
      try {
        const payload = {
          tripId: this.rebookTrip.tripId,
          productId: product.productId,
          pickupEta: product.pickupEta || 0,
          dropEta: (product.trip && product.trip.dropEta) || 0,
        };
        if (product.fareId) payload.fareId = product.fareId;

        /*
         * The mode is a hint. The server re-derives it from its own clock before
         * calling Uber - a pickup that has already passed dispatches now
         * whatever this says.
         */
        if (this.schedule && !this.sendNow && this.schedule.pickupTime) {
          payload.pickupTime = this.schedule.pickupTime;
          payload.mode = this.bookingTypeOf(product) === "RESERVE" ? "reserve" : "scheduled";
        } else {
          payload.mode = "on_demand";
        }
        if (this.schedule) {
          if (this.schedule.serverTime) payload.estimatedAt = this.schedule.serverTime;
          payload.dispatchNow = this.sendNow;
        }

        const response = await axios.post("uber/trip/create", payload);
        toast.success((response.data && response.data.message) || "Booked. A new Uber is on the way.");

        uberRealtime.clearCritical(this.rebookTrip.tripId);
        this.closeRebook();
        this.fetchTrips();
        this.$emit("rebooked", response.data);
      } catch (err) {
        // The row stays on the tab: the trip is still booking_failed until a
        // booking actually succeeds, so there is nothing to put back here.
        toast.error(this.readError(err, "Could not book this trip."));
      } finally {
        this.booking = false;
      }
    },

    /* ---------------------------------------------------------------- *
     * Helpers
     * ---------------------------------------------------------------- */

    /** A trip Uber refused outright - no booking was ever made. */
    isBookingFailed(trip) {
      const status = (trip && (trip.uberStatus || (trip.uberDetails || {}).uberStatus)) || "";
      return String(status).toLowerCase() === UBER_STATUS.BOOKING_FAILED;
    },

    /** Uber's reason for refusing, when there is one. */
    failureReasonOf(trip) {
      return (trip && (trip.failureReason || (trip.uberDetails || {}).failureReason)) || "";
    },

    bookingTypeOf(product) {
      const value = String(product.advanceBookingType || "").toUpperCase();
      return ["RESERVE", "SCHEDULED", "FLEXIBLE"].includes(value) ? value : "ONDEMAND";
    },

    bookingTypeLabel(product) {
      const type = this.bookingTypeOf(product);
      return type === "ONDEMAND" ? "ON DEMAND" : type;
    },

    durationOf(product) {
      const seconds = (product.trip || {}).durationEstimate;
      if (!seconds) return "--";
      return `${Math.round(seconds / 60)} min`;
    },

    addressOf(place) {
      if (!place) return "";
      return place.address || "";
    },

    serviceTypeName(id) {
      if (id === 1) return "Pickup";
      if (id === 2) return "Drop off";
      return "";
    },

    /** Pickup and reporting times arrive as epoch values. */
    toMoment(value) {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "number" || /^\d+$/.test(String(value))) return moment(Number(value));
      const parsed = moment(value);
      return parsed.isValid() ? parsed : null;
    },

    formatDate(value) {
      const parsed = this.toMoment(value);
      return parsed && parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "--";
    },

    onDateChange() {
      if ((this.filter.dateRange || []).length === 2) {
        this.dateModal = false;
        this.getTripListing();
      }
    },

    reset() {
      this.filter = this.defaultFilter();
      this.getTripListing();
    },

    readError(err, fallback) {
      return readApiError(err, fallback);
    },
  },
};
</script>

<style lang="scss" scoped>
// Fills whatever height the tab shell gives it - no assumption about the
// surrounding chrome.
.attention-wrap {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  // Measured and published by UberTrips; the calc is only for the first paint.
  height: var(--uber-tab-height, calc(100vh - 190px));
  box-sizing: border-box;
  overflow: hidden;
}

.filter-outerblk {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  flex-shrink: 0;
}

.single-fieldsouter {
  display: flex;
  align-items: center;
  flex: 1 1 190px;
  max-width: 230px;
}

.search-field {
  flex: 1 1 280px;
  max-width: 320px;
}

.reset-outer {
  display: flex;
  align-items: center;
}

.reset-btn {
  font-weight: 600;
}

.table-outerblk {
  margin-top: 14px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  overflow: hidden;
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  ::v-deep .v-data-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  th {
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6a6a6a !important;
    white-space: nowrap;
  }

  td {
    font-size: 13px;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.critical-row {
  background: #fff8f8;
}

.clickable-row {
  cursor: pointer;
}

.stack-cell {
  display: flex;
  flex-direction: column;
}

.strong-text {
  font-weight: 600;
  color: #333333;
}

.muted-text {
  font-size: 11px;
  color: #8a8a8a;
}

.address-cell {
  max-width: 240px;
  white-space: normal;
}

.status-chip {
  padding: 2px 9px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* Uber's refusal, under the chip. Clamped to two lines with the full text on
   hover - some of these run to a paragraph and would stretch the row. */
.reason-detail {
  margin-top: 4px;
  max-width: 220px;
  font-size: 11px;
  line-height: 1.35;
  color: #8a8a8a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rebook-btn {
  color: #ffffff !important;
  font-weight: 600;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  color: #b0b0b0;
  font-size: 14px;

  i {
    font-size: 32px;
    color: #9fd6b0;
  }
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  margin-top: 14px;
  padding: 6px 16px;
  flex-shrink: 0;
}

.count-text {
  position: absolute;
  left: 16px;
  font-size: 12px;
  color: #8a8a8a;
}

/* ------------------------------------------------------------------ *
 * Re-book dialog - same Uber theme as the booking dialog
 * ------------------------------------------------------------------ */

::v-deep .uber-estimate-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.22) !important;
}

/*
 * Bounded by the screen, same as the booking dialog in CreateTrip.
 *
 * This is the re-book dialog and it is built the same way, so it had the same
 * fault: sized from its contents, inside a dialog carrying `overflow: hidden`,
 * which clips the excess instead of scrolling it. On a short laptop the footer -
 * and with it Confirm Ride - was not rendered at all.
 */
.uber-estimate-card {
  border-radius: 16px !important;
  overflow: hidden;
  font-family: "UberMove", "UberMoveText", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #fff;

  display: flex;
  flex-direction: column;
  max-height: 90vh;
  min-height: 0;
}

.uep-header {
  /* Holds its size; the card scrolls its middle, not its edges. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #000;
  padding: 14px 18px;
}

.uep-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.uep-wordmark-img {
  height: 36px;
  width: auto;
  display: block;
  object-fit: contain;
}

.uep-header-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
}

.uep-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  letter-spacing: -0.5px;
}

.uep-subtitle {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 4px;
}

.uep-subtitle-row {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}

.uep-close-btn {
  opacity: 0.6 !important;
}

.uep-close-btn:hover {
  opacity: 1 !important;
}

.uep-schedule {
  /* Fixed: the pickup time stays in view while the list scrolls. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 18px;
  background: #f7f7f7;
  border-bottom: 1px solid #ebebeb;
}

.uep-schedule-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uep-schedule-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a8a8a;
  font-weight: 600;
}

.uep-schedule-value {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.uep-schedule-lead {
  font-size: 12px;
  color: #6b6b6b;
}

// Plain-language note about what will actually happen, under the pickup strip.
.uep-note {
  /* Fixed: this is the warning that the cab goes right away. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  font-size: 12px;
  background: #fffaf0;
  color: #8a6d1f;
  border-bottom: 1px solid #ebebeb;

  strong {
    font-weight: 700;
  }

  &--now {
    background: #f0fdf9;
    color: #0f766e;
  }
}

/*
 * The scrolling middle of this dialog.
 *
 * Unlike the booking dialog, this one is `scrollable`, so Vuetify makes the
 * v-card-text the scroll region and the ride list inside it keeps its own cap.
 * It still needs min-height: 0 to be allowed to shrink - without it the card
 * grows past 90vh and the footer is clipped again.
 */
.uep-body {
  padding: 16px 18px !important;
  background: #fff;

  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.choice-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.choice-card {
  flex: 1 1 240px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border-color: #1a1a1a;
  }

  &.selected {
    border-color: #1a1a1a;
    background: #fafafa;
    box-shadow: 0 0 0 1px #1a1a1a inset;
  }
}

.choice-name {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.2px;
}

.choice-hint {
  font-size: 12px;
  color: #8a8a8a;
}

.inline-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 14px;
  background: #fff4f4;
  border-left: 3px solid #c62828;
  border-radius: 4px;
  color: #8e1c1c;
  font-size: 13px;
}

.uep-loading,
.uep-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 34px 24px;
  gap: 10px;
  background: #fff;
}

.uep-loading-title,
.uep-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;
}

.uep-list {
  background: #fff;
  padding: 8px 0 4px;
  margin-top: 8px;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
}

.uep-row {
  display: flex;
  align-items: center;
  padding: 12px 4px;
  cursor: pointer;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 4px;
    right: 4px;
    height: 1px;
    background: #f0f0f0;
  }

  &:hover {
    background: #fafafa;
  }

  &--selected {
    background: #f5f5f5 !important;

    .uep-check {
      background: #000;
    }
  }
}

.uep-img-wrap {
  flex-shrink: 0;
  width: 110px;
  height: 64px;
  background-size: cover;
  background-position: center;
  background-color: #f6f6f6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 16px;
}

.uep-vehicle-img {
  width: 100px;
  height: 56px;
  object-fit: contain;
}

.uep-info {
  flex: 1;
  min-width: 0;
}

.uep-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uep-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.5px;
}

.uep-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #ececec;
  color: #5a5a5a;
  white-space: nowrap;

  &--RESERVE {
    background: #e8eaf6;
    color: #3f51b5;
  }

  &--SCHEDULED {
    background: #e3f2fd;
    color: #1565c0;
  }

  &--FLEXIBLE {
    background: #f3e5f5;
    color: #7b1fa2;
  }
}

.uep-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.uep-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b6b6b;
}

.uep-meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #c8c8c8;
}

.uep-time-rows {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 5px;
}

.uep-eta-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #555;
  font-weight: 500;

  strong {
    font-weight: 700;
    color: #1a1a1a;
  }

  &:last-child {
    color: #276ef1;
    strong {
      color: #276ef1;
    }
  }
}

.uep-check {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;

  &--on {
    background: #000 !important;
  }

  .v-icon {
    opacity: 0.5;
  }

  &--on .v-icon {
    opacity: 1 !important;
  }
}

.uep-footer {
  /* Never shrinks - Confirm Ride is the reason the dialog exists. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid #ebebeb;
  background: #fff;
}

/* Short screens: trim the fixed parts so the ride list keeps a usable height.
   Same treatment as the booking dialog. */
@media (max-height: 720px) {
  .uep-header {
    padding: 9px 18px;
  }

  .uep-schedule,
  .uep-note {
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .uep-body {
    padding: 10px 18px !important;
  }

  .uep-footer {
    padding: 9px 18px;
  }
}

.uep-btn-cancel {
  font-size: 14px !important;
  color: #6b6b6b !important;
  font-weight: 500 !important;
  text-transform: none !important;
}

.uep-btn-confirm {
  background: #000 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  padding: 0 24px !important;
  height: 40px !important;
  text-transform: none !important;
  min-width: 180px !important;
}
</style>
